import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { FieldAnswer, SelectChoiceField } from '@app/api';
import { SubmitButton } from '@app/core';
import { ErrorMsg, useRequiredMsg } from '@app/form';

import { SingleChoice } from '../Fields/SingleChoice';
import { SubmitType } from '../fieldConfigurations';

interface Props {
  field: SelectChoiceField;
  onSubmit: (values: FieldAnswer, helpers?: FormikHelpers<FieldAnswer>) => any;
  submitType?: SubmitType;
}

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    textAlign: 'center',
    marginTop: theme.marginBase * 7,
  },
}));

const emptyChoice = { label: '', ref: '' };
export const buildSingleChoiceFieldValue = (
  field: SelectChoiceField,
): FieldAnswer => {
  return {
    type: 'choice',
    field: {
      id: field.id,
      type: field.type,
      ref: field.ref,
      title: field.title,
    },
    choice: emptyChoice,
  };
};

export const SingleChoiceFieldForm = ({
  field,
  onSubmit,
  submitType = SubmitType.DONE,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const requiredMsg = useRequiredMsg();

  const initialValues = useMemo(
    () => buildSingleChoiceFieldValue(field),
    [field],
  );
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        choice: Yup.object().test('required', requiredMsg, (choice: any) => {
          if (!field.validation || !field.validation.required) {
            return true;
          }
          return !!(choice.label && choice.ref);
        }),
      }),
    [field, t],
  );

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      key={field.id}
    >
      {({ values }) => (
        <Form>
          <div>
            <SingleChoice field={field} />
            <ErrorMsg name="choice" />
            {submitType === SubmitType.CONTINUE ? (
              <div className={classes.button}>
                <SubmitButton
                  withNextArrow
                  fullMobileWidth
                  trackingName="Screener Next"
                  disabled={
                    field.validation &&
                    field.validation.required &&
                    (!values.choice || !values.choice.label)
                  }
                >
                  <Trans i18nKey={'screeningForm.continue'} />
                </SubmitButton>
              </div>
            ) : (
              <div className={classes.button}>
                <SubmitButton
                  color="green"
                  trackingName="Screener Finished"
                  fullMobileWidth
                  disabled={
                    field.validation &&
                    field.validation.required &&
                    (!values.choice || !values.choice.label)
                  }
                >
                  <Trans i18nKey={'screeningForm.end'} />
                </SubmitButton>
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};
